import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatError } from "../lib/api";
import { ShieldCheck, User } from "lucide-react";
import Logo from "../components/Logo";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("student"); // student | admin
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await login(email.trim(), password);
            navigate(u.role === "admin" ? "/admin" : "/app/storefront");
        } catch (e) {
            setErr(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 mb-8 group" data-testid="login-brand">
                    <Logo height={96} />
                </Link>

                <div className="bg-white border border-parchment rounded-2xl p-8 shadow-auth">
                    <h1 className="font-serif text-3xl font-semibold mb-1">Welcome back.</h1>
                    <p className="text-ink/60 mb-6">Sign in to your account.</p>

                    <div className="grid grid-cols-2 rounded-full bg-paper border border-parchment p-1 mb-6">
                        <button
                            data-testid="login-tab-student"
                            onClick={() => setTab("student")}
                            className={`flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold transition-colors ${tab === "student" ? "bg-ink text-paper" : "text-ink/60"}`}
                        >
                            <User className="h-4 w-4" /> Student
                        </button>
                        <button
                            data-testid="login-tab-admin"
                            onClick={() => setTab("admin")}
                            className={`flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold transition-colors ${tab === "admin" ? "bg-ink text-paper" : "text-ink/60"}`}
                        >
                            <ShieldCheck className="h-4 w-4" /> Admin
                        </button>
                    </div>

                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <label className="label-mini">Email</label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input-field"
                                placeholder={tab === "admin" ? "samrudhaa395@gmail.com" : "you@school.edu"}
                            />
                        </div>
                        <div>
                            <label className="label-mini">Password</label>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                                placeholder="••••••••"
                            />
                        </div>
                        {err && (
                            <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                {err}
                            </div>
                        )}
                        <button data-testid="login-submit-btn" type="submit" disabled={loading} className="btn-primary w-full">
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    {tab === "student" && (
                        <div className="text-center text-sm text-ink/60 mt-6">
                            New here?{" "}
                            <Link to="/register" className="text-brick font-semibold hover:underline" data-testid="login-goto-register">
                                Create a student account
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
